import React from 'react'
import { Box, Container } from '@mui/material'

import type { HolidayCollectionIndexEntry } from '@/src/types/holidays'
import HeaderContainer from '@/src/components/common/header/HeaderRouter'
import MobileHeaderContainer from '@/src/components/common/header/variants/mobile'
import DesktopFooter from '@/src/components/desktop/homepageFooter'
import MobileFooter from '@/src/components/mobile/homepageFooter'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'

import CollectionIndexWrapper from '@/src/components/common/holidays/collectionIndex'
import AppTitleBarWrapper from '@/src/components/common/holidays/appTitleBar'
import { HOLIDAYS_MAX_WIDTH } from '@/src/utils/holidays'

interface HolidayIndexPageProps {
    entries: HolidayCollectionIndexEntry[]
    isMobile: boolean
    domainCountry: string
    /** Inside the native app: its title bar replaces the site header, and there is no footer. */
    isApp: boolean
    appTitle: string
}

/**
 * The index is a single link list with no device-specific layout, so the two trees differ only in
 * which header and footer they mount. Splitting it into two page files would duplicate the list
 * markup for no behavioural difference — see the device-split note in the surface doc.
 */
const HolidayIndexPage: React.FC<HolidayIndexPageProps> = ({ entries, isMobile, domainCountry, isApp, appTitle }) => {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <ErrorBoundaryWrapper identifier={`holidays-index-header-${isMobile ? 'mobile' : 'desktop'}`}>
                {isApp && <AppTitleBarWrapper title={appTitle} isRoot />}
                {!isApp && isMobile && <MobileHeaderContainer domainCountry={domainCountry} />}
                {!isApp && !isMobile && <HeaderContainer domainCountry={domainCountry} activeVertical="holidays" />}
            </ErrorBoundaryWrapper>

            <Container component="main" maxWidth={false} sx={{ maxWidth: HOLIDAYS_MAX_WIDTH, py: isMobile ? 3 : 5 }}>
                <ErrorBoundaryWrapper identifier="holidays-index-list">
                    <CollectionIndexWrapper entries={entries} isMobile={isMobile} />
                </ErrorBoundaryWrapper>
            </Container>

            {!isApp && (
                <Box sx={{ mt: 'auto' }}>
                    {isMobile ? <MobileFooter showHomeHighlights={false} /> : <DesktopFooter showHomeHighlights={false} />}
                </Box>
            )}
        </Box>
    )
}

export { HolidayIndexPage }
export default HolidayIndexPage
